import type { OnboardingStepName } from "@/constants/Onboarding";
import { OrderedOnboardingSteps } from "@/constants/Onboarding";
import { BaseActiveOnboardingCardContent } from "@/ui/components/onboarding/v2/cards/BaseActiveOnboardingCardContent";
import { BaseOnboardingCard } from "@/ui/components/onboarding/v2/cards/BaseOnboardingCard";
import { getOnboardingStepContent } from "@/ui/components/onboarding/v2/content/OnboardingStepContent";
import { Button, Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import ArrowBulletSVG from "@/ui/theme/icons/illustrations/ArrowBulletSVG";
import { getOnboardingIllustrationIcon } from "@/ui/theme/icons/illustrations/onboarding";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

enum ArrowBulletSize {
  Height = 12,
  Width = 18,
}

interface ListItemProps extends ViewWithTestID {
  text: string;
}

const ListItem = ({ testID, text }: ListItemProps) => (
  <HStack alignItems="flex-start" gap={8}>
    <Box paddingTop={2}>
      <ArrowBulletSVG
        height={ArrowBulletSize.Height}
        width={ArrowBulletSize.Width}
      />
    </Box>
    <Box flex={1}>
      <Text color={ColorToken.NeutralPrimary} testID={testID} variant="caption">
        {text}
      </Text>
    </Box>
  </HStack>
);

interface ActiveOnboardingCardProps extends ViewWithTestID {
  onActionPress: () => void;
  patientName?: string;
  stepName: OnboardingStepName;
}

const ActiveOnboardingCard = ({
  onActionPress,
  patientName,
  stepName,
  testID,
}: ActiveOnboardingCardProps) => {
  const { button, description, duration, iconName, listItems, title } =
    getOnboardingStepContent({ patientName, stepName });

  // +2: +1 for zero-index, +1 because "Initial" (always pre-completed) is step 1 but not in OrderedOnboardingSteps
  const stepNumber = OrderedOnboardingSteps.indexOf(stepName) + 2;

  return (
    <BaseOnboardingCard borderRadius="large" hasShadow testID={testID}>
      <BaseActiveOnboardingCardContent
        action={
          <Button
            isFullWidth
            onPress={onActionPress}
            rightIcon={button.rightIcon}
            testID={testID}
            title={button.title}
            variant={button.variant}
          />
        }
        caption={`Step ${stepNumber}`}
        description={description}
        duration={duration}
        icon={getOnboardingIllustrationIcon({ iconName })}
        testID={testID}
        title={title}
      >
        <VStack gap={8}>
          {listItems.map((item) => (
            <ListItem
              key={item}
              testID={joinTestIDs(testID, `item__${listItems.indexOf(item)}`)}
              text={item}
            />
          ))}
        </VStack>
      </BaseActiveOnboardingCardContent>
    </BaseOnboardingCard>
  );
};

export type { ActiveOnboardingCardProps };
export { ActiveOnboardingCard };
